import Price from "components/common/Price";
import { classNames, insertBeforeLastOccurrence, truncateString } from "lib/utils/helpers";
import { Minus, Plus, Trash } from "@phosphor-icons/react";
import defaultPackageImage from "assests/images/package.png";
import { AddOnMetaType } from "lib/types/checkout";
import { QuantitySelector } from "./QuantitySelector";
import { IconButton } from "@shopflo/ui/icon-button";
import { OrderSummaryConfigType } from "lib/types/merchantConfigTypes";
import { getTextColorByBrightness, hexToRgba } from "@shopflo/ui/utils";
import { useItemCardAlign } from "lib/hooks/useItemCardAlign";
import { Icon } from "@shopflo/ui";

export const AddOnItem = ({
  variant,
  addOnData,
  parentTitle,
  handleAddOnDelete,
  disableActions = false,
  allowDelete = true,
  itemQuantity = 1,
  customisations,
  isItemPropertiesAvailable,
}: {
  variant: "CART" | "CHECKOUT";
  addOnData: AddOnMetaType;
  parentTitle?: string;
  handleAddOnDelete?: () => void;
  disableActions?: boolean;
  allowDelete?: boolean;
  itemQuantity?: number;
  customisations?: OrderSummaryConfigType;
  isItemPropertiesAvailable?: boolean;
}) => {
  const price = addOnData?.price || 0;
  const imageStyle = {
    CART: "h-[72px] w-[72px]",
    CHECKOUT: "h-[4.188rem] w-16",
  };

  const containerStyle = {
    CART: "flex w-full min-w-full flex-row justify-between rounded-xl items-start",
    CHECKOUT: "flex w-full min-w-full flex-row justify-between",
  };

  const isRowAligned = useItemCardAlign(variant === "CART") === "row";

  return (
    <div className={classNames(containerStyle[variant])}>
      <div
        className="h-[72px] w-[72px] border-1 rounded-lg overflow-hidden"
        style={{
          borderColor: getTextColorByBrightness({
            bgHexColor: customisations?.backgroundColor ?? "#FFFFFF",
            lightOpacity: 24,
          }),
        }}>
        <img
          src={insertBeforeLastOccurrence(addOnData?.image ?? "", ".jpg", "_small")}
          alt={"add-on-image"}
          className={classNames("object-cover rounded-lg h-full w-full", imageStyle[variant])}
          onError={({ currentTarget }) => {
            currentTarget.onerror = null; // prevents looping
            currentTarget.src = defaultPackageImage;
          }}
        />
      </div>
      {isRowAligned || isItemPropertiesAvailable ? (
        <>
          <div className="flex  flex-1 ml-3 justify-between pr-1 gap-6 min-h-[72px]">
            <div>
              <p
                className={classNames(
                  "overflow-hidden text-ellipsis  pt-0.5 text-text-xs-medium text-zinc-700",
                  !parentTitle ? "line-clamp-2 text-wrap" : "line-clamp-1 text-wrap",
                )}
                style={{
                  color: customisations?.textColor,
                }}>
                {truncateString(addOnData?.product_name, 40)}
              </p>
              {parentTitle && (
                <h4 className="text-text-xs-regular text-zinc-500 line-clamp-1 text-wrap">
                  For {truncateString(parentTitle, 40)}
                </h4>
              )}
            </div>

            <div className="flex flex-col gap-1 items-end">
              <Price
                size="md"
                total={price * itemQuantity}
                orientation="horizontal"
                customization={{
                  textColor: customisations?.textColor,
                  freebieTextColor: customisations?.offerBadgeColor,
                }}
              />
              <div className="ml-auto shrink-0  items-end flex justify-end ">
                <button
                  onClick={handleAddOnDelete}
                  disabled={disableActions}
                  className={classNames(
                    "flex h-7 flex-row w-full items-center justify-center gap-1 rounded ",
                    disableActions ? "cursor-not-allowed opacity-50" : "",
                  )}
                  style={{
                    backgroundColor: customisations?.backgroundColor,
                  }}>
                  <Icon
                    IconComponent={Trash}
                    size="xxs"
                    iconProps={{
                      weight: "bold",
                      color: hexToRgba(customisations?.textColor.substring(0, 7) ?? "#3F3F46", 0.6),
                    }}
                    className="text-zinc-500"
                  />
                  <span
                    className="text-xs font-medium text-coal-dark whitespace-nowrap"
                    style={{
                      color: hexToRgba(customisations?.textColor.substring(0, 7) ?? "#3F3F46", 0.6),
                    }}>
                    Remove
                  </span>
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col flex-1 ml-3 justify-between pr-1">
          <div className="flex flex-row justify-between items-center space-x-1 text-text-sm-semibold min-h-[36px]">
            <div>
              <p
                className={classNames(
                  "overflow-hidden text-ellipsis  pt-0.5 text-text-xs-medium text-zinc-700",
                  !parentTitle ? "line-clamp-2 text-wrap" : "line-clamp-1 text-wrap",
                )}
                style={{
                  color: customisations?.textColor,
                }}>
                {truncateString(addOnData?.product_name, 40)}
              </p>
              {parentTitle && (
                <h4 className="text-text-xs-regular text-zinc-500 line-clamp-1 text-wrap">
                  For {truncateString(parentTitle, 40)}
                </h4>
              )}
            </div>
            <Price
              size="md"
              total={price * itemQuantity}
              orientation="horizontal"
              customization={{
                textColor: customisations?.textColor,
                freebieTextColor: customisations?.offerBadgeColor,
              }}
            />
          </div>
          <div
            className={classNames(
              "flex flex-row justify-start items-center gap-2 pt-1",
              isRowAligned || isItemPropertiesAvailable ? "justify-end" : "justify-start",
            )}>
            {
              <IconButton
                IconComponent={Trash}
                disabled={disableActions}
                variant="outline"
                size="xs"
                className="!bg-transparent w-7 h-7"
                iconProps={{
                  weight: "bold",
                  className: "text-zinc-400 h-3.5 w-3.5",
                }}
                onClick={handleAddOnDelete}
              />
            }

            <QuantitySelector
              quantity={itemQuantity}
              customisations={customisations}
              isCartItem
              isDecrementDisabled
              isIncrementDisabled
              isRowAligned={isRowAligned || isItemPropertiesAvailable}
              onDelete={handleAddOnDelete}
            />
          </div>
        </div>
      )}
    </div>
  );
};
